import { FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";

import { Modal } from "@/components";
import { VideoWrapper } from "@/components/VideoWrapper/VideoWrapper";

import { UnipileConnectType } from "@/pages/Private/pages/Account/hooks/useUnipileConnect";

import { FirstStepItem } from "../utils/firstStepsToItems";
import { LEARN_CONTENT } from "../constants/learnContent";

interface LearnMoreModalProps {
	/** The onboarding item whose "Learn more" was opened, or `null` when closed. */
	item: FirstStepItem | null;
	onClose: () => void;
	/** Starts the Unipile connect flow for connection items. */
	onConnect: (type: UnipileConnectType) => void;
}

/**
 * Onboarding "Learn more" modal. Renders the topic copy (icon, title,
 * subtitle, HTML body and optional in-app video) in the shared Modal. The
 * primary CTA performs the item's action (navigate or start the connect flow);
 * the cancel button just dismisses.
 */
export const LearnMoreModal: FunctionComponent<LearnMoreModalProps> = ({
	item,
	onClose,
	onConnect,
}) => {
	const navigate = useNavigate();

	const topic = item ? LEARN_CONTENT[item.learnTopic] : undefined;

	if (!item || !topic) {
		return null;
	}

	const handleCta = () => {
		if (item.connect) {
			onClose();
			onConnect(item.connect);

			return;
		}

		navigate(item.to);
		onClose();
	};

	return (
		<Modal
			cancelButtonText="Cancel"
			handleClose={onClose}
			handleSave={handleCta}
			icon={
				<span
					className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-action-200 text-brand-light shrink-0"
					dangerouslySetInnerHTML={{ __html: topic.icon }}
				/>
			}
			isOpened={Boolean(item)}
			size="sm"
			submitButtonText={topic.cta}
			subtitle={topic.sub}
			title={topic.title}
			titleWeight="bold"
		>
			<div className="flex flex-col gap-4">
				{topic.videoId && (
					<VideoWrapper
						className="!w-full !h-auto aspect-video !rounded-lg overflow-hidden"
						youtubeId={topic.videoId}
					/>
				)}
				<div
					className="text-sm text-text [&_em]:italic [&_h4]:mb-2 [&_h4]:mt-4 [&_h4]:font-semibold [&_li]:mb-1 [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
					dangerouslySetInnerHTML={{ __html: topic.body }}
				/>
			</div>
		</Modal>
	);
};
